import { Table } from '@octano/global-ui';
import { useCallback, useContext, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Input, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../components/text';
import { TranslationsKeys } from '../../../../../locales/translations';
import { ModalityCodesEnum } from '../../../../SectionsMaintainer/enums/modality-codes.enum';
import { CourseFormContext } from '../../providers/CourseProvider';
import {
  CourseAttendance,
  CourseFormFields,
  CourseFormMode,
} from '../../types';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';

type CourseAttendanceSectionProps = {
  readOnly?: boolean;
};

/** Sentinel `activityTypeId` para la fila del componente online (sin actividad). */
const ONLINE_ROW_ID = -1;

/**
 * Fila de la tabla "Horas y Asistencia Requerida por Componente".
 *
 * ISS-04 — Cada fila lleva SUS PROPIOS valores actuales. El `<Table>` de
 * `@octano/global-ui` memoiza las celdas por identidad del objeto fila; si los
 * `cellFormat` leyeran del closure (`attendance.find(...)`) los `<Input>`
 * controlados quedarían congelados en modo edición. Al reconstruir las filas en
 * cada cambio (`useMemo` dependiente de los valores del form) la identidad
 * cambia y las celdas se vuelven a renderizar de forma reactiva.
 */
type AttendanceRow = {
  activityTypeId: number;
  label: string;
  totalHours: number | null;
  weeklyHours: number | null;
  minPercentage: number;
  isOnline: boolean;
};

/**
 * HU 15624 — Filtra keystrokes a solo dígitos. Devuelve `null` para input
 * vacío, o un entero >= 0. Garantiza que `totalHours`/`weeklyHours` nunca
 * acepten decimales, negativos ni no-numéricos (FRD §1.3.b + CP-07..09).
 */
const sanitizeIntegerHours = (raw: string): number | null => {
  const digitsOnly = raw.replace(/\D+/g, '');
  if (digitsOnly === '') {
    return null;
  }
  const parsed = parseInt(digitsOnly, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const CourseAttendanceSection = ({
  readOnly = false,
}: CourseAttendanceSectionProps) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);

  const {
    control,
    watch,
    setValue,
    getValues,
  } = useFormContext<CourseFormFields>();

  const { mode, formData } = useContext(CourseFormContext);
  const { activities = [], modalities = [] } = formData;

  /**
   * ISS-04 + feedback post-PR-278 — Gestión de `attendance` sin registrar el
   * field aquí (NO `useController`, NO `<Controller>`):
   *
   * - **LECTURA** vía `useWatch`: subscriber puro que se re-renderiza cuando
   *   el `<Controller name="attendance">` de `CourseActivityTypeSection` agrega
   *   o quita componentes (checkboxes).
   *
   * - **ESCRITURA** vía `setValue` con `shouldDirty/shouldTouch`: notifica al
   *   `<Controller>` sibling y a este `useWatch`. El bug original ISS-04 venía
   *   de que las cellFormat del `<Table>` de `@octano/global-ui` congelaban
   *   `attendance` por closure — ya no aplica porque cada `AttendanceRow`
   *   transporta sus propios valores.
   *
   * Doble registro (useController aquí + Controller en CourseActivityTypeSection)
   * ROMPE la reactividad del checkbox→tabla porque RHF no propaga updates entre
   * registros hermanos del mismo `name`.
   *
   * El `?? []` vive dentro de un `useMemo` para estabilizar la identidad del
   * array cuando RHF devuelve `undefined` (caso inicial) y así no invalidar
   * las deps de `validActivityTypes` y `tableData` en cada render.
   */
  const watchedAttendance = useWatch({ control, name: 'attendance' });
  const attendance = useMemo(() => watchedAttendance ?? [], [
    watchedAttendance,
  ]);

  const [
    modality,
    onlineAttendancePercentage,
    onlineTotalHours,
    onlineWeeklyHours,
  ] = watch([
    'modality',
    'onlineAttendancePercentage',
    'onlineTotalHours',
    'onlineWeeklyHours',
  ]);

  const validActivityTypes = useMemo(
    () =>
      activities?.filter((e) =>
        attendance?.map((a) => a.activityTypeId).includes(+e.value),
      ) ?? [],
    [activities, attendance],
  );

  const selectedModality = useMemo(() => {
    if (!modality?.value) {
      return null;
    }
    const modalityData = modalities.find((m) => m.value === modality.value);
    return modalityData;
  }, [modality, modalities]);

  const showOnlineComponent = useMemo(() => {
    // @ts-ignore - deliveryMode exists in the modality data from API
    const deliveryMode = selectedModality?.deliveryMode;
    return (
      deliveryMode === ModalityCodesEnum.OL ||
      deliveryMode === ModalityCodesEnum.SP
    );
  }, [selectedModality]);

  /**
   * ISS-03 + ISS-04 — Filas de la tabla unificada. Los componentes regulares
   * (con `activityTypeId`) se mapean desde `attendance`; el componente online
   * se agrega como ÚLTIMA fila cuando la modalidad lo requiere y escribe a los
   * form fields `onlineTotalHours`/`onlineWeeklyHours`/`onlineAttendancePercentage`.
   *
   * Cada objeto fila se reconstruye en cada cambio para garantizar reactividad
   * de los `<Input>` controlados (ver doc de `AttendanceRow`).
   */
  const tableData = useMemo<AttendanceRow[]>(() => {
    const componentRows: AttendanceRow[] = validActivityTypes.map(
      (activity) => {
        const item = attendance?.find(
          (att) => att.activityTypeId === +activity.value,
        );
        return {
          activityTypeId: +activity.value,
          label: activity.label,
          totalHours: item?.totalHours ?? null,
          weeklyHours: item?.weeklyHours ?? null,
          minPercentage: item?.minPercentage ?? 0,
          isOnline: false,
        };
      },
    );

    if (!showOnlineComponent) {
      return componentRows;
    }

    const onlineRow: AttendanceRow = {
      activityTypeId: ONLINE_ROW_ID,
      label: t('onlineComponent'),
      totalHours: onlineTotalHours ?? null,
      weeklyHours: onlineWeeklyHours ?? null,
      minPercentage: onlineAttendancePercentage ?? 0,
      isOnline: true,
    };
    return [...componentRows, onlineRow];
  }, [
    attendance,
    validActivityTypes,
    showOnlineComponent,
    onlineTotalHours,
    onlineWeeklyHours,
    onlineAttendancePercentage,
    t,
  ]);

  /**
   * Lee `attendance` en vivo con `getValues` (evita closures stale) y escribe
   * con `setValue` + `shouldDirty: true` para que el `<Controller>` sibling
   * (CourseActivityTypeSection) reciba la notificación y mantenga su estado
   * sincronizado.
   */
  const updateAttendanceItem = useCallback(
    (activityTypeId: number, patch: Partial<CourseAttendance>) => {
      const current = getValues('attendance') ?? [];
      const index = current.findIndex(
        (att) => att.activityTypeId === activityTypeId,
      );
      if (index < 0) {
        return;
      }
      const next = [...current];
      next[index] = { ...next[index], ...patch };
      setValue('attendance', next, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue],
  );

  /**
   * ISS-03 — Maneja el cambio de horas (totales/semanales). La fila online
   * escribe a sus form fields propios; las demás al array `attendance`.
   */
  const onChangeIntegerHours = useCallback(
    (row: AttendanceRow, key: 'totalHours' | 'weeklyHours', raw: string) => {
      const sanitized = sanitizeIntegerHours(raw);
      if (row.isOnline) {
        setValue(
          key === 'totalHours' ? 'onlineTotalHours' : 'onlineWeeklyHours',
          sanitized,
        );
        return;
      }
      updateAttendanceItem(row.activityTypeId, { [key]: sanitized });
    },
    [setValue, updateAttendanceItem],
  );

  /**
   * ISS-03 — Maneja el cambio del % de asistencia. La fila online escribe a
   * `onlineAttendancePercentage`; las demás al array `attendance`.
   */
  const onChangeAttendance = useCallback(
    (row: AttendanceRow, value: string) => {
      if (isNaN(+value)) {
        return;
      }
      if (+value < 0 || +value > 100) {
        return;
      }
      if (row.isOnline) {
        setValue('onlineAttendancePercentage', +value);
        return;
      }
      updateAttendanceItem(row.activityTypeId, { minPercentage: +value });
    },
    [setValue, updateAttendanceItem],
  );

  if (!activities?.length) {
    return <></>;
  }

  const isDisabled = readOnly || mode === CourseFormMode.WATCH;

  return (
    <Row>
      <Col xs={12} className="pt-4">
        <SectionTitle text={t(`assistanceRequired`)} />
      </Col>
      {mode !== CourseFormMode.WATCH && (
        <Col xs={12}>
          <p className="fs-16 fw-300">{t(`assistanceRequiredSubtitle`)}</p>
        </Col>
      )}
      <Col xs={12}>
        <Table
          columns={[
            {
              columnName: 'label',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`activityType`),
            },
            {
              columnName: 'totalHours',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`componentTotalHours`),
              cellFormat: ({ row }) => (
                <div
                  className="d-flex m-auto align-items-center"
                  style={{ maxWidth: '80px' }}
                >
                  <Input
                    name={`totalHours_${row.label}`}
                    type="text"
                    inputMode="numeric"
                    onChange={(e) =>
                      onChangeIntegerHours(row, 'totalHours', e.target.value)
                    }
                    value={row.totalHours ?? ''}
                    disabled={isDisabled}
                  />
                </div>
              ),
            },
            {
              columnName: 'weeklyHours',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`componentWeeklyHours`),
              cellFormat: ({ row }) => (
                <div
                  className="d-flex m-auto align-items-center"
                  style={{ maxWidth: '80px' }}
                >
                  <Input
                    name={`weeklyHours_${row.label}`}
                    type="text"
                    inputMode="numeric"
                    onChange={(e) =>
                      onChangeIntegerHours(row, 'weeklyHours', e.target.value)
                    }
                    value={row.weeklyHours ?? ''}
                    disabled={isDisabled}
                  />
                </div>
              ),
            },
            {
              columnName: 'minPercentage',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`obligatoryAttendance`),
              cellFormat: ({ row }) => (
                <div
                  className="d-flex m-auto align-items-center"
                  style={{ maxWidth: '80px' }}
                >
                  <Input
                    name={`attendance_${row.label}`}
                    type="text"
                    onChange={(e) => {
                      onChangeAttendance(row, e.target.value);
                    }}
                    value={row.minPercentage ?? 0}
                    disabled={isDisabled}
                  />
                  <span className="pl-2">%</span>
                </div>
              ),
            },
          ]}
          data={tableData}
          noResultsText={
            <TableEmptyContent
              title={t('assistanceRequiredSection.emptyState')}
              subtitle=""
            />
          }
        />
      </Col>
    </Row>
  );
};

export default CourseAttendanceSection;
